/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: performances entity configuration
 * Generator: scripts/generate-api-clients.ts
 *
 * This file contains the API client for the performances table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the performances API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, performance_name, start_datetime, max_contestants, is_slack, round_id, is_published, created_by, created_at, updated_by, updated_at]
 * Searchable fields: []
 * Filterable fields: [id, round_id]
 */

import { httpClient } from '../http-client.js';
import type { UnifiedQueryOptions } from '@config/search/global-search-config';
import type {
  PerformancesRow,
  PerformancesInsert,
  PerformancesUpdate
} from '../../types/generated_schemas/generated-performances-schema.js';

export interface PerformancesQueryOptions extends UnifiedQueryOptions {}

export interface PerformancesResponse {
  data: PerformancesRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for performances table operations
 *
 * @description Provides type-safe access to performances API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class PerformancesClient {
  private readonly basePath = '/performances';

  /**
   * Get all performances records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of performances records
   */
  async getAll(options?: PerformancesQueryOptions): Promise<PerformancesRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<PerformancesResponse>(url);
    return response.data;
  }

  /**
   * Get a single performances record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the performances record
   */
  async getById(id: string): Promise<PerformancesRow> {
    const response = await httpClient.get<{
      data: PerformancesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new performances record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created performances record
   */
  async create(data: PerformancesInsert): Promise<PerformancesRow> {
    const response = await httpClient.post<{
      data: PerformancesRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing performances record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated performances record
   */
  async update(id: string, data: PerformancesUpdate): Promise<PerformancesRow> {
    const response = await httpClient.put<{
      data: PerformancesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a performances record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple performances records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created performances records
   */
  async createMany(data: PerformancesInsert[]): Promise<PerformancesRow[]> {
    const response = await httpClient.post<{
      data: PerformancesRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Delete multiple performances records
   *
   * @param ids - Array of unique identifiers for records to delete
   * @returns Promise resolving when deletion is complete
   */
  async deleteMany(ids: string[]): Promise<void> {
    await httpClient.delete(`${this.basePath}/batch`, { ids });
  }

  /**
   * Search performances with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    _query: string,
    _columns?: string[],
    _options?: PerformancesQueryOptions
  ): Promise<PerformancesResponse> {
    throw new Error('Search operations are not available for performances - no searchable fields configured');
  }

  /**
   * Get count of performances records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a performances record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'performances',
      publicFields: ['id', 'performance_name', 'start_datetime', 'max_contestants', 'is_slack', 'round_id', 'is_published', 'created_by', 'created_at', 'updated_by', 'updated_at'],
      searchableFields: [],
      filterableFields: ['id', 'round_id'],
      sortableFields: ['created_at', 'updated_at'],
      readonlyFields: ['id', 'created_at', 'updated_at'],
      requiredFields: ['id'],
    };
  }
}

// Export singleton instance for convenience
export const performancesClient = new PerformancesClient();
